import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../utils/api.js'
import { withIfMatch } from '../utils/concurrency.js'
import type { Loan, LoanList, QueryParams, Item } from '../types/domain'

// Raw `GET /loans` envelope: either a bare array or `{ data, items?, overdue_count }`.
interface LoansEnvelope {
  data?: Loan[]
  items?: Loan[]
  overdue_count?: number
  pagination?: LoanList['pagination']
  [key: string]: unknown
}

// ── Loans ────────────────────────────────────────────────
export function useLoans(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['loans', params],
    queryFn: () => api.get<Loan[] | LoansEnvelope>('/loans', { params }).then((r): LoanList => {
      const d = r.data
      // Backend returns { data: [...], pagination: {...}, overdue_count: N }
      // Consumers expect { items: [...], overdue_count: N }
      if (Array.isArray(d)) return { items: d, overdue_count: 0 }
      return { ...d, items: d.items || d.data || [] }
    }),
    refetchInterval: 5 * 60 * 1000,
  })
}
export function useReturnLoan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (itemId: number | string) => api.post<Loan>(`/loans/${itemId}/return`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loans'] })
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
export function useCreateLoanReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/loans/reminders', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loans'] }),
  })
}

interface CreateLoanInput {
  id: number | string
  version?: number | null
  [key: string]: unknown
}

export function useCreateLoan() {
  const qc = useQueryClient()
  return useMutation({
    // Creating a loan is a PUT on the versioned items resource (finding M3):
    // strip `version` from the variables and send it in the `If-Match` header.
    mutationFn: ({ id, version, ...loanData }: CreateLoanInput) =>
      api.put<Item>(`/items/${id}`, { status: 'on-loan', ...loanData }, withIfMatch(version)).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loans'] })
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
